import React, { useEffect, useState } from 'react';
import { Button, Space, Input, Form, Select, Row, Col, Tag, Typography, notification, Tooltip, Modal } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { TableCustom } from '../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { newObjectKeys, formatDateVN } from '../../utils/utility';
import { getConfigs } from '../../services/config';
import isEqual from 'lodash/isEqual';

const itemName = 'Cấu hình';
const createItemLink = '/cms/quan-ly-cau-hinh/them-cau-hinh';
const editItemLink = '/cms/quan-ly-cau-hinh/sua-cau-hinh';
const { Text } = Typography;

const TYPE_OPTIONS = [
  { value: -1, label: 'Tất cả' },
  { value: 1, label: 'Chuỗi' },
  { value: 2, label: 'JSON' },
  { value: 3, label: 'Ảnh' },
  { value: 4, label: 'HTML' },
  { value: 5, label: 'Slide' },
  { value: 6, label: 'Banner' },
  { value: 7, label: 'Thư mục ảnh' },
];

const STATUS_OPTIONS = [
  { value: -1, label: 'Tất cả' },
  { value: 1, label: 'Hoạt động', color: 'green' },
  { value: 0, label: 'Không hoạt động', color: 'red' },
  { value: 2, label: 'Đã xóa', color: 'volcano' },
];

const ConfigList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({ page: 1, limit: 20 });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  // helper: convert HTML to plain text and truncate
  const toPlain100 = (html, limit = 100) => {
    if (!html || typeof html !== 'string') return '';
    const el = document.createElement('div');
    el.innerHTML = html;
    const text = (el.textContent || el.innerText || '').trim();
    if (text.length <= limit) return text;
    return text.slice(0, limit) + '...';
  };
  const toPlain = (html) => {
    if (!html || typeof html !== 'string') return '';
    const el = document.createElement('div');
    el.innerHTML = html;
    return (el.textContent || el.innerText || '').trim();
  };
  const shortValue = (val, type) => {
    if (val == null) return '';
    if (Number(type) === 4) {
      // HTML
      return toPlain100(String(val), 120);
    }
    if (Number(type) === 2) {
      // JSON
      try {
        const obj = typeof val === 'string' ? JSON.parse(val) : val;
        const s = JSON.stringify(obj);
        return s.length > 120 ? s.slice(0, 120) + '...' : s;
      } catch (e) {
        const s = String(val);
        return s.length > 120 ? s.slice(0, 120) + '...' : s;
      }
    }
    if (Number(type) === 1) {
      const s = String(val);
      return s.length > 120 ? s.slice(0, 120) + '...' : s;
    }
    if (Number(type) === 5) {
      try {
        const arr = typeof val === 'string' ? JSON.parse(val) : val;
        const len = Array.isArray(arr) ? arr.length : 0;
        return `${len} slides`;
      } catch (e) {
        return '0 slides';
      }
    }
    if (Number(type) === 6) {
      return 'Banner';
    }
    if (Number(type) === 7) {
      try {
        const arr = typeof val === 'string' ? JSON.parse(val) : val;
        const len = Array.isArray(arr) ? arr.length : 0;
        return `${len} ảnh`;
      } catch (e) {
        return '0 ảnh';
      }
    }
    return '';
  };
  const tooltipFullText = (val, type) => {
    if (val == null) return '';
    if (Number(type) === 4) return toPlain(String(val));
    if (Number(type) === 2) {
      try {
        const obj = typeof val === 'string' ? JSON.parse(val) : val;
        return JSON.stringify(obj);
      } catch (e) {
        return String(val);
      }
    }
    if (Number(type) === 1) return String(val);
    if (Number(type) === 5) {
      try {
        const arr = typeof val === 'string' ? JSON.parse(val) : val;
        if (!Array.isArray(arr)) return '';
        const titles = arr.map((s, i) => `${i + 1}. ${s?.title || '(không tiêu đề)'}`);
        return titles.join('\n');
      } catch (e) {
        return '';
      }
    }
    if (Number(type) === 6) {
      try {
        const obj = typeof val === 'string' ? JSON.parse(val) : val;
        return obj?.link ? `Link: ${obj.link}` : 'Banner';
      } catch (e) {
        return 'Banner';
      }
    }
    if (Number(type) === 7) {
      try {
        const arr = typeof val === 'string' ? JSON.parse(val) : val;
        if (!Array.isArray(arr)) return '';
        const first = arr.slice(0, 5).map((u, i) => `${i + 1}. ${u}`);
        const more = arr.length > 5 ? `\n... và ${arr.length - 5} ảnh khác` : '';
        return first.join('\n') + more;
      } catch (e) {
        return '';
      }
    }
    return '';
  };

  const openDetail = (record) => {
    setDetailRecord(record);
    setDetailOpen(true);
  };

  // Initialize params from URL on first load
  useEffect(() => {
    const page = Number(searchParams.get('page')) || 1;
    const limit = Number(searchParams.get('limit')) || 20;
    const keyword = searchParams.get('keyword') || undefined;
    const statusStr = searchParams.get('status');
    const statusVal = statusStr !== null ? Number(statusStr) : -1;
    const typeStr = searchParams.get('type');
    const typeVal = typeStr !== null ? Number(typeStr) : -1;

    const next = { page, limit };
    if (keyword) Object.assign(next, { keyword });
    if (statusVal >= 0) Object.assign(next, { status: [statusVal] });
    if (typeVal >= 0) Object.assign(next, { type: typeVal });

    setParams(next);
    formSearch.setFieldsValue({
      keyword: keyword,
      status: statusVal >= 0 ? statusVal : -1,
      type: typeVal >= 0 ? typeVal : -1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push params to URL when they change
  useEffect(() => {
    if (!params) return;
    const sp = {
      page: String(params.page || 1),
      limit: String(params.limit || 20),
    };
    if (params.keyword) sp.keyword = params.keyword;
    if (Array.isArray(params.status) && params.status.length > 0 && params.status[0] >= 0) {
      sp.status = String(params.status[0]);
    }
    if (typeof params.type === 'number' && params.type >= 0) {
      sp.type = String(params.type);
    }
    setSearchParams(sp, { replace: true });
  }, [params, setSearchParams]);

  const { data: dataConfigs, isPending, refetch } = useQuery({ queryKey: ['dataConfigs', params], queryFn: () => getConfigs(params) });

  // Removed delete mutation to disable deletion from the list

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.keyword) Object.assign(temp, { keyword: values?.keyword });
    if (values?.type >= 0) Object.assign(temp, { type: values?.type });
    if (values?.status >= 0) Object.assign(temp, { status: [values?.status] });

    if (!isEqual(temp, params)) {
      setParams(temp);
      return;
    }
    refetch();
  };

  const typeLabel = (t) => TYPE_OPTIONS.find((x) => x.value === t)?.label || t;
  const statusMeta = (s) => STATUS_OPTIONS.find((x) => x.value === s) || {};

  const columns = [
    {
      title: 'STT',
      dataIndex: 'id',
      key: 'stt',
      width: 70,
      fixed: 'left',
      align: 'center',
      ellipsis: true,
      onHeaderCell: () => ({ style: { maxWidth: 70 } }),
      onCell: () => ({ style: { maxWidth: 70, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }),
      render: (_, __, index) => (params?.page - 1) * params?.limit + index + 1,
    },
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      render: (_, { key: k }) => <Text code>{k}</Text>,
      minWidth: 160,
      width: 220,
      fixed: 'left',
    },
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      key: 'title',
      render: (_, { title, id }) => (
        <Link to={`${editItemLink}/${id}`} className="text-[#1677FF]">{title}</Link>
      ),
      minWidth: 200,
      width: 300,
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      key: 'type',
      render: (type) => <span>{typeLabel(type)}</span>,
      minWidth: 120,
      width: 160,
    },
    {
      title: 'Giá trị (rút gọn)',
      dataIndex: 'value',
      key: 'short_value',
      render: (_, record) => {
        const t = Number(record?.type);
        let bannerThumb = null;
        if (t === 6) {
          try {
            const obj = typeof record?.value === 'string' ? JSON.parse(record.value) : record.value;
            const img = obj?.image;
            if (img && typeof img === 'string') {
              bannerThumb = (
                <img
                  src={img}
                  alt="banner-thumb"
                  style={{ height: 32, width: 'auto', objectFit: 'contain', borderRadius: 4, background: '#f2f3f5', border: '1px solid #f0f0f0' }}
                />
              );
            }
          } catch (e) {
            // ignore parse error, fallback to text below
          }
        }
        return (
          <Space size={6}>
            <Tooltip overlayInnerStyle={{ maxWidth: 600 }} placement="topLeft" title={tooltipFullText(record?.value, record?.type)}>
              {bannerThumb ? bannerThumb : <Text>{shortValue(record?.value, record?.type)}</Text>}
            </Tooltip>
            {(t === 1 || t === 2 || t === 4 || t === 5 || t === 6 || t === 7) && (
              <Button type="text" size="small" onClick={() => openDetail(record)}>
                <EyeOutlined />
              </Button>
            )}
          </Space>
        );
      },
      minWidth: 260,
      width: 340,
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note',
      render: (note) => <Text>{note}</Text>,
      minWidth: 200,
      width: 280,
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (_, { updated_at }) => <Space direction="vertical">{updated_at && <Text className="text-[#667085]">{formatDateVN(updated_at)}</Text>}</Space>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag className="px-2 py-1" color={statusMeta(status)?.color}>{statusMeta(status)?.label || status}</Tag>
      ),
      width: 100,
      fixed: 'right',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size={0}>
          <Button type="text" onClick={() => navigate(`${editItemLink}/${record?.id}`)}>
            <EditOutlined />
          </Button>
        </Space>
      ),
      width: 70,
      fixed: 'right',
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Danh sách {itemName}</h1>
        {/* <Link to={createItemLink}>
          <Button type="primary">
            <PlusOutlined />
            Thêm {itemName}
          </Button>
        </Link> */}
      </div>

      <Form name="search_configs" form={formSearch} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="keyword" label={<Text>Từ khóa</Text>}>
              <Input placeholder="Nhập từ khóa tìm kiếm..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="type" label={<Text>Loại</Text>} initialValue={-1}>
              <Select showSearch options={TYPE_OPTIONS} optionFilterProp="label" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select showSearch options={STATUS_OPTIONS} optionFilterProp="label" />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit">
              <SearchOutlined />
              Tìm kiếm
            </Button>
            {params && Object.values(newObjectKeys(params, ['keyword', 'status', 'type'])).some((value) => value) && (
              <Button
                htmlType="button"
                onClick={() => {
                  formSearch.resetFields();
                  setParams({ page: 1, limit: params.limit });
                }}
              >
                <DeleteOutlined />
                Xóa tìm kiếm
              </Button>
            )}
          </Col>
        </Row>
      </Form>

      <TableCustom columns={columns} dataSource={dataConfigs?.data ?? []} totalItem={dataConfigs?.total ?? 0} loading={isPending} params={params} setParams={setParams} />
      <Modal
        title={detailRecord ? `${detailRecord.title} (${detailRecord.key})` : 'Xem chi tiết'}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        {detailRecord && Number(detailRecord.type) === 4 && (
          <div
            style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, maxHeight: 500, overflow: 'auto' }}
            dangerouslySetInnerHTML={{ __html: String(detailRecord.value || '') }}
          />
        )}
        {detailRecord && Number(detailRecord.type) === 2 && (
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 500, overflow: 'auto', background: '#fafafa', padding: 12, borderRadius: 6 }}>
            {(() => {
              try {
                const obj = typeof detailRecord.value === 'string' ? JSON.parse(detailRecord.value) : detailRecord.value;
                return JSON.stringify(obj, null, 2);
              } catch (e) {
                return String(detailRecord.value || '');
              }
            })()}
          </pre>
        )}
        {detailRecord && Number(detailRecord.type) === 1 && (
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 500, overflow: 'auto', background: '#fafafa', padding: 12, borderRadius: 6 }}>
            {String(detailRecord.value || '')}
          </pre>
        )}
        {detailRecord && Number(detailRecord.type) === 6 && (
          <div style={{ maxHeight: 600, overflow: 'auto' }}>
            {(() => {
              try {
                const obj = typeof detailRecord.value === 'string' ? JSON.parse(detailRecord.value) : detailRecord.value;
                if (!obj || typeof obj !== 'object') return <Text>Không có banner.</Text>;
                return (
                  <div className="space-y-3">
                    {obj?.image && (
                      <img
                        src={obj.image}
                        alt="banner"
                        style={{ width: '100%', height: 'auto', maxHeight: 480, objectFit: 'contain', borderRadius: 8, background: '#f2f3f5', border: '1px solid #f0f0f0' }}
                      />
                    )}
                    {obj?.link && (
                      <div>
                        <Text strong>Link: </Text>
                        <a href={obj.link} target="_blank" rel="noreferrer" className="text-[#1677FF]" style={{ fontSize: 14 }}>
                          {obj.link}
                        </a>
                      </div>
                    )}
                  </div>
                );
              } catch (e) {
                return <Text>Không thể hiển thị banner.</Text>;
              }
            })()}
          </div>
        )}
        {detailRecord && Number(detailRecord.type) === 7 && (
          <div style={{ maxHeight: 600, overflow: 'auto' }}>
            {(() => {
              try {
                const arr = typeof detailRecord.value === 'string' ? JSON.parse(detailRecord.value) : detailRecord.value;
                if (!Array.isArray(arr) || arr.length === 0) return <Text>Không có ảnh.</Text>;
                return (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                    {arr.map((u, i) => (
                      <div key={i} className="space-y-2">
                        <img src={u} alt={`img-${i}`} style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 6, background: '#f2f3f5', border: '1px solid #f0f0f0' }} />
                        <a href={u} target="_blank" rel="noreferrer" className="text-[#1677FF]" style={{ fontSize: 12 }}>
                          Mở ảnh
                        </a>
                      </div>
                    ))}
                  </div>
                );
              } catch (e) {
                return <Text>Không thể hiển thị thư mục ảnh.</Text>;
              }
            })()}
          </div>
        )}
        {detailRecord && Number(detailRecord.type) === 5 && (
          <div style={{ maxHeight: 500, overflow: 'auto' }}>
            {(() => {
              try {
                const arr = typeof detailRecord.value === 'string' ? JSON.parse(detailRecord.value) : detailRecord.value;
                if (!Array.isArray(arr) || arr.length === 0) return <Text>Không có slide.</Text>;
                return (
                  <div className="space-y-3">
                    {arr.map((s, i) => (
                      <div key={i} className="p-2 border rounded-md flex items-center gap-3">
                        {s?.image && <img src={s.image} alt={s?.title || ''} style={{ height: 48, width: 'auto', objectFit: 'cover', borderRadius: 4, background: '#f2f3f5' }} />}
                        <div className="flex-1">
                          <div style={{ fontWeight: 600 }}>{s?.title || '(không tiêu đề)'}</div>
                          {s?.link && <div className="text-[#667085]" style={{ fontSize: 12 }}>{s.link}</div>}
                        </div>
                        <div className="text-[#667085]" style={{ fontSize: 12 }}>Order: {s?.order ?? i + 1}</div>
                      </div>
                    ))}
                  </div>
                );
              } catch (e) {
                return <Text>Không thể hiển thị slides.</Text>;
              }
            })()}
          </div>
        )}
        {/* No fallback text for unsupported types */}
      </Modal>
    </div>
  );
};

export default ConfigList;
